/**
 * API Client Configuration et Interceptors
 */

import { httpClient } from '../httpClient';
import { logger } from '../logger';

/**
 * Configurer les interceptors globaux
 */
export const setupApiClient = () => {
  // Ajouter le token d'authentification si disponible
  const token = sessionStorage.getItem('orion_access_token') || localStorage.getItem('auth_token');
  if (token) {
    // Les options sont passées par appel, pas de global interceptor dans notre impl
  }

  logger.info('[API] Client configured');
};

/**
 * Helper pour les calls API avec gestion d'erreur centralisée
 */
export const apiCall = async <T,>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  endpoint: string,
  body?: any,
  options?: any,
  retried = false
): Promise<T> => {
  try {
    const token = sessionStorage.getItem('orion_access_token') || localStorage.getItem('auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const result =
      method === 'GET'
        ? await httpClient.get<T>(endpoint, { ...options, headers })
        : method === 'POST'
          ? await httpClient.post<T>(endpoint, body, { ...options, headers })
          : method === 'PUT'
            ? await httpClient.put<T>(endpoint, body, { ...options, headers })
            : await httpClient.delete<T>(endpoint, { ...options, headers });

    if (!result.ok) {
      const canRefresh =
        !retried &&
        result.status === 401 &&
        endpoint.indexOf('action=login') === -1 &&
        endpoint.indexOf('action=refresh') === -1;

      if (canRefresh) {
        const refreshToken = localStorage.getItem('orion_crm_refresh_token');
        if (refreshToken) {
          try {
            const refreshRes = await fetch('api/index.php?action=refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken }),
            });
            if (refreshRes.ok) {
              const refreshData = await refreshRes.json();
              if (refreshData?.token) {
                localStorage.setItem('auth_token', refreshData.token);
                if (refreshData.refreshToken) {
                  localStorage.setItem('orion_crm_refresh_token', refreshData.refreshToken);
                }
                return apiCall<T>(method, endpoint, body, options, true);
              }
            }
          } catch (e) {
            logger.warn('[Auth] Refresh failed', e);
          }
        }
      }

      const error = new Error(result.error || `API Error: ${result.status}`);
      logger.error(`[${method}] ${endpoint}`, error, { status: result.status, endpoint });
      throw error;
    }

    logger.debug(`[${method}] ${endpoint} ✓`, { status: result.status });
    if (result.data === undefined) {
      throw new Error(`Empty response payload for ${endpoint}`);
    }
    return result.data;
  } catch (error) {
    logger.error(`[${method}] ${endpoint} Failed`, error as Error, { endpoint });
    throw error;
  }
};

export default setupApiClient;
